import { useState, useCallback } from 'react';
import { userTickerListApi } from '../services/userTickerListApi';
import { 
  clearUserListsCache,
  clearListCache,
  clearCurrentListNameCache,
  clearCurrentListIdCache,
} from '../utils/listCache';

interface UseModalsParams {
  currentListId: number | null;
  isAuthenticated: boolean;
  userLists: Array<{ id: number; list_name: string }>;
  needsUserListsReload: React.MutableRefObject<boolean>;
  handleListChange: (listId: number) => Promise<void>;
  refreshUsageLimitsRef: React.MutableRefObject<(() => void) | null>;
  onAfterDelete?: () => void; // Called after successful deletion for page-specific cleanup
}

interface UseModalsReturn {
  // Modal state
  isDeleteModalOpen: boolean;
  isErrorDialogOpen: boolean;
  errorMessage: string;
  
  // Handlers
  handleDeleteList: () => void;
  handleDeleteCancel: () => void;
  handleDeleteConfirm: () => Promise<void>;
  handleErrorDialogClose: () => void;
  showError: (message: string) => void;
}

/**
 * Hook for managing modals (delete confirmation and error dialog) across List* pages.
 * Handles delete list confirmation logic that is common across all pages.
 */
export const useModals = ({
  currentListId,
  isAuthenticated,
  userLists,
  needsUserListsReload,
  handleListChange,
  refreshUsageLimitsRef,
  onAfterDelete,
}: UseModalsParams): UseModalsReturn => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleDeleteList = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!currentListId || !isAuthenticated) return;

    try {
      // Delete the list
      await userTickerListApi.deleteList(currentListId);
      
      // Clear caches
      clearUserListsCache();
      if (currentListId) {
        clearListCache(currentListId);
      }
      clearCurrentListNameCache();
      clearCurrentListIdCache();
      
      // Mark that we need to reload user lists
      needsUserListsReload.current = true;
      
      // Find the next list to navigate to
      const currentIndex = userLists.findIndex(list => list.id === currentListId);
      let nextListId: number | null = null;
      
      if (userLists.length > 1) {
        if (currentIndex < userLists.length - 1) {
          // Navigate to the next list below
          nextListId = userLists[currentIndex + 1].id;
        } else {
          // Navigate to the list above (previous list)
          nextListId = userLists[currentIndex - 1].id;
        }
      }
      
      if (nextListId) {
        // Navigate to the next list
        await handleListChange(nextListId);
      } else {
        // No more lists, reset to default state
        if (onAfterDelete) {
          onAfterDelete();
        }
      }
      
      setIsDeleteModalOpen(false);
      
      // Refresh usage limits after successful deletion
      if (refreshUsageLimitsRef.current) {
        refreshUsageLimitsRef.current();
      }
    } catch (error) {
      console.error('Failed to delete list:', error);
      // Show user-friendly error message
      const errorMsg = error instanceof Error ? error.message : String(error);
      if (errorMsg.includes('Cannot delete the only list')) {
        setErrorMessage('Cannot delete your only list. You must have at least one list.');
      } else {
        setErrorMessage('Failed to delete list. Please try again.');
      }
      setIsDeleteModalOpen(false);
      setIsErrorDialogOpen(true);
    }
  }, [
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete,
  ]);

  const handleErrorDialogClose = useCallback(() => {
    setIsErrorDialogOpen(false);
    setErrorMessage('');
  }, []);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setIsErrorDialogOpen(true);
  }, []);

  return {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
    showError,
  };
};

